import { coerce }  from '@meltwater/coerce';
import { GydaDocument } from '../../gyda-document/index.js';
import { CUSTOM_KEY_METRIC, isValidKeyMetric } from '../../key-metric/index.js';
import { DocumentListItem } from './list-item';
import { SeriesData } from '@meltwater/mw-visualizations-core';
import { DocumentListOptions } from '../options/index.js';
import { version } from '../../version.js';

const [ MAJOR_VERSION ] = version.split('.');
const DOCUMENT_KEY_FOR_UNAVAILABLE_TITLE_AND_BODY = '';

export const DOCUMENTS_LOADED_EVENT_NAME = 'ua-documents-loaded';
const DOCUMENT_LIST_ITEM_HEIGHT = 57;

function estimateListHeight({ documentCount }) {
    return documentCount * DOCUMENT_LIST_ITEM_HEIGHT;
}

/**
 * This visualizes an array of Gyda documents as a single column of compact
 * documents suitable for inclusion in dashboards and reports.
 *
 * @example
 * const list = document.createElement(DocumentList.tagName);
 * list.setData(seriesData, { companyId, documents });
 * list.setOptions({ keyMetric: 'ave', language: 'en', onPointClick: ({ key }) => alert(`Key clicked: ${key}`), yAxisTitle: 'USD' });
 * container.appendChild(list);
 */
export class DocumentList extends HTMLElement {
    #companyId;
    #documents = [];
    #documentList;
    #seriesData;
    #vizOptions = {};

    /**
     * The tag name for the automatically-defined custom element.
     *
     * The value is specific to a major version so as to provide forward
     * compatibility by avoiding collisions with other copies of the library
     * that may be defined as well.
     *
     * @returns {string} The major version-specific tag name for the defined custom element
     */
    static get tagName() {
        return `pr-document-list-v${MAJOR_VERSION}`;
    }

    #buildDocumentList() {
        const fluxContentCards = this.#documents
            .map((gydaDocument, index) => {
                if (!gydaDocument) {
                    return undefined;
                }

                const li = document.createElement('li');
                const contentCard = document.createElement(DocumentListItem.tagName);

                const key = this.#seriesData?.data[index]?.key === gydaDocument.documentId
                    ? DOCUMENT_KEY_FOR_UNAVAILABLE_TITLE_AND_BODY
                    : this.#seriesData?.data[index]?.key;

                contentCard.setData({
                    companyId: this.#companyId,
                    document: gydaDocument,
                    key,
                    keyMetric: this.#vizOptions.keyMetric,
                    keyMetricLabel: this.#vizOptions.yAxisTitle,
                    language: this.#vizOptions.language,
                    onPointClick: this.#vizOptions.onPointClick,
                    value: this.#seriesData?.data[index].value
                });

                li.appendChild(contentCard);
                return li;
            })
            .filter(element => Boolean(element));

        this.#documentList.replaceChildren(...fluxContentCards);

        const documentCount = fluxContentCards.length;
        const estimatedHeight = estimateListHeight({ documentCount });

        const documentListLoadedEvent = new CustomEvent(DOCUMENTS_LOADED_EVENT_NAME, {
            detail: {
                documentCount: fluxContentCards.length,
                estimatedHeight
            },
            bubbles: true,
            cancelable: true,
            composed: true
        });

        this.dispatchEvent(documentListLoadedEvent);
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML = /*html*/`
            <style>
                :host {
                    display: flex;
                    overflow: auto;
                    --flux-spacing-3: 4px;
                }
                ol, li {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    width: 100%;
                }
                li {
                    border-bottom: 1px solid #E0E0E0;
                }
            </style>
            <ol id="document-list"></ol>
        `;

        this.#documentList = this.shadowRoot.querySelector('#document-list');
    }

    /**
     * Render the list according to the data and options when attaching to
     * the DOM.
     *
     * @returns {undefined}
     */
    connectedCallback() {
        this.render();
    }

    /**
     * Set the data to display in the document list
     *
     * @example
     * const list = document.createElement(DocumentList.tagName);
     * list.setData(seriesData, { companyId, documents });
     *
     * @param {SeriesData} [seriesData=null] - The series data for any key metric values to display. The order **must** align with the documents. If provided, the key value will be shown as the document title.
     * @param {object} metadata - The metadata
     * @param {string} metadata.companyId - The companyId to use for access permission with the premium document viewer
     * @param {GydaDocument[]} metadata.documents - The documents to render in the list
     *
     * @returns {undefined}
     */
    setData(seriesData = null, { companyId, documents }) {
        this.#companyId = companyId;
        this.#documents = documents.map(document => (document !== null ? coerce(document, GydaDocument, 'document must be a GydaDocument') : null));
        this.#seriesData = seriesData !== null
            ? coerce(seriesData, SeriesData, 'seriesData must be coerce-able to SeriesData')
            : null;
    }

    /**
     * Apply optional customizations to the display of the document list.
     *
     * @example
     * const list = document.createElement(DocumentList.tagName);
     * list.setOptions({ keyMetric: 'ave', language: 'en', onPointClick: ({ key }) => alert(`Key clicked: ${key}`), yAxisTitle: 'USD' });
     *
     * @param {DocumentListOptions} options - The options to apply to the document list
     *
     * @returns {undefined}
     */
    setOptions(options = {}) {
        const vizOptions = coerce(options, DocumentListOptions, 'options must be valid DocumentListOptions');

        if (!isValidKeyMetric(vizOptions.keyMetric)) {
            throw new RangeError(`A valid key metric value or constant must be provided. Provided value: ${vizOptions.keyMetric?.toString()}`);
        }

        if (vizOptions.keyMetric === CUSTOM_KEY_METRIC && (this.#seriesData === null || vizOptions.yAxisTitle === undefined)) {
            throw new RangeError(`A custom key metric value requires seriesData for the metric values and a yAxisTitle for the metric label. Provided seriesData length: ${this.#seriesData?.data.length}; Provided yAxisTitle: ${vizOptions.yAxisTitle}`);
        }

        this.#vizOptions = vizOptions;
    }

    /**
     * Render the list according to its data and options.
     *
     * This is only necessary to handle changes *after* the element has been
     * attached to the DOM.
     *
     * @returns {undefined}
     */
    render() {
        this.#buildDocumentList();
    }
}

if (!customElements.get(DocumentList.tagName)) {
    customElements.define(DocumentList.tagName, DocumentList);
}
